/**
 * The Stores tab's Hiring column: every store's live hiring links, read with ONE request
 * for the whole page — never one per store.
 *
 * `status`:
 *   - `off`     hiring is not shown (not enabled, not allowed, or not answered yet):
 *               nothing is asked and the page looks as it always did.
 *   - `loading` the first answer for this project is on its way.
 *   - `ready`   `byStore` holds every live link, keyed by store id in lower case. A
 *               store that is not in the map has no hiring linked.
 *   - `failed`  the list could not be read in full, and there is no earlier list of this
 *               project to show. The column shows a dash, never "No hiring linked": that
 *               would be a statement the page cannot back.
 *
 * A project normally answers in one page of 500. More than that is followed by its
 * cursor to the end, however many pages that is: a cap would turn the whole column to
 * dashes, for everyone, on the day a project passed it. A page that fails, or a cursor
 * that does not move on, is `failed` as a whole — half a list would show stores with a
 * link as having none.
 *
 * A refusal that says hiring is off (404) or not this user's to see (403) is passed to
 * the shared access answer, which turns the column off here and on every other screen.
 *
 * `reload()` re-reads without clearing what is on show, and a re-read that fails leaves
 * it on show: the list read a moment ago is still the best the page knows, and dashes in
 * every row would take away what the person was just looking at. `apply(link)` puts one
 * link the server has just answered with (a new link, one that was stopped, or one moved
 * to another store, which leaves the store it was at) into the list at once, so its row
 * does not wait for the re-read. An answer for a project the page has since left is dropped.
 *
 * `raise` is what the same answer says about raising hiring (phase 2): whether it is on
 * for the project, and whether THIS person may raise. Both are false until the list has
 * answered, when it failed, and from a server that does not say — so the Raise control
 * is never drawn on a guess.
 *
 * `cancel` is the same for cancelling an opening in Recruitment: whether Cancel is on for
 * the project, and whether THIS person may cancel. False on every doubt, for the same
 * reason — the Cancel control is never drawn on a guess.
 */
import { useCallback, useEffect, useState } from 'react';
import { hiringLinksAPI, type HiringLink, type HiringLinkList } from '../../services/api';
import { hiringStatusOf, isHiringOff } from './hiringText';
import { forgetHiringAccess } from './useHiringAccess';

export const PAGE_LIMIT = 500;

export type StoreHiringStatus = 'off' | 'loading' | 'ready' | 'failed';

/** Whether hiring can be raised from ChannelPlex here, as the list answered it. */
export interface RaiseAccess { enabled: boolean; canRaise: boolean }

export const NO_RAISE: RaiseAccess = { enabled: false, canRaise: false };

/** What a list answer says about raising hiring. Anything but a plain `true` is a no. */
export const raiseAccessOf = (list: Pick<HiringLinkList, 'raise'> | null | undefined): RaiseAccess => {
  const enabled = list?.raise?.enabled === true;
  const canRaise = enabled && list?.raise?.canRaise === true;
  return enabled ? { enabled, canRaise } : NO_RAISE;
};

/** Whether an opening can be cancelled in Recruitment from ChannelPlex here, as the list answered it. */
export interface CancelAccess { enabled: boolean; canCancel: boolean }

export const NO_CANCEL: CancelAccess = { enabled: false, canCancel: false };

/** What a list answer says about cancelling. Anything but a plain `true` is a no. */
export const cancelAccessOf = (list: Pick<HiringLinkList, 'cancel'> | null | undefined): CancelAccess => {
  const enabled = list?.cancel?.enabled === true;
  const canCancel = enabled && list?.cancel?.canCancel === true;
  return enabled ? { enabled, canCancel } : NO_CANCEL;
};

export interface StoreHiring {
  status: StoreHiringStatus;
  byStore: ReadonlyMap<string, HiringLink[]>;
  raise: RaiseAccess;
  cancel: CancelAccess;
  reload: () => void;
  apply: (link: HiringLink) => void;
}

const NONE: ReadonlyMap<string, HiringLink[]> = new Map();

/** A store with no live link: one shared empty list, so a row's props stay the same between renders. */
export const NO_HIRING_LINKS: HiringLink[] = [];

/** How a store id is compared: as text, whatever case it arrived in. */
export const storeHiringKey = (storeId: unknown): string => String(storeId ?? '').toLowerCase();

/** The store a link belongs to, by id only. A name or a code can be shared, or changed since the link was made. */
const storeKeyOf = (link: HiringLink): string | null => {
  const storeId = link.subject?.kind === 'store' ? link.subject.storeId : null;
  return storeId ? storeHiringKey(storeId) : null;
};

const groupByStore = (links: HiringLink[]): Map<string, HiringLink[]> => {
  const byStore = new Map<string, HiringLink[]>();
  for (const link of links) {
    const key = storeKeyOf(link);
    if (!key) continue;
    const list = byStore.get(key);
    if (list) list.push(link);
    else byStore.set(key, [link]);
  }
  return byStore;
};

/**
 * The list with one link put in (newest first, as the API lists them) or, once it is closed,
 * taken out. A link is followed by one store only: it leaves whichever store had it before, so
 * an opening moved to another store ("Move to another store") is no longer shown at the old one.
 * The question asked when someone is deployed (DeployHiringCheck) keeps its few stores' lists the same way.
 */
export const withLink = (byStore: ReadonlyMap<string, HiringLink[]>, link: HiringLink): ReadonlyMap<string, HiringLink[]> => {
  const key = storeKeyOf(link);
  if (!key) return byStore;
  const next = new Map<string, HiringLink[]>();
  for (const [store, links] of byStore) {
    // Only a store that had it gets a new list: every other row keeps its own.
    const rest = links.some((l) => l.id === link.id) ? links.filter((l) => l.id !== link.id) : links;
    if (rest.length > 0) next.set(store, rest);
  }
  if (link.state !== 'closed') next.set(key, [link, ...(next.get(key) ?? [])]);
  return next;
};

/**
 * Every page of the project's live store links, or a throw. `wanted` is asked before
 * each page, so a list nobody is waiting for any more is not read to its end. With
 * `storeId`, one store's live links only (the question asked when someone is deployed reads
 * the one or two stores it is about, never the whole project).
 */
export const readLiveStoreLinks = async (
  projectId: number, wanted: () => boolean, storeId?: string,
): Promise<{ links: HiringLink[]; raise: RaiseAccess; cancel: CancelAccess }> => {
  const links: HiringLink[] = [];
  const cursors = new Set<string>();
  let cursor: string | null = null;
  // Said by every page alike; the first page's word is kept.
  let raise: RaiseAccess | null = null;
  let cancel: CancelAccess | null = null;
  while (wanted()) {
    const res = await hiringLinksAPI.list(
      projectId,
      { state: 'live', subject: 'store', ...(storeId ? { storeId } : {}), limit: PAGE_LIMIT, ...(cursor ? { cursor } : {}) },
      { background: true },
    );
    const data = res?.data?.data;
    if (!data || !Array.isArray(data.links)) throw new Error('unreadable hiring list');
    links.push(...data.links);
    raise = raise ?? raiseAccessOf(data);
    cancel = cancel ?? cancelAccessOf(data);
    cursor = data.nextCursor ?? null;
    if (!cursor) return { links, raise, cancel };
    // The same cursor twice would be the same page for ever; so would a page that
    // brings nothing and still names a next one.
    if (cursors.has(cursor) || data.links.length === 0) throw new Error('hiring list cursor did not move on');
    cursors.add(cursor);
  }
  throw new Error('hiring list no longer wanted');
};

export const useStoreHiring = (projectId: number | null | undefined, enabled: boolean): StoreHiring => {
  const [state, setState] = useState<{
    projectId: number | null; status: StoreHiringStatus; byStore: ReadonlyMap<string, HiringLink[]>; raise: RaiseAccess; cancel: CancelAccess;
  }>({ projectId: null, status: 'off', byStore: NONE, raise: NO_RAISE, cancel: NO_CANCEL });
  const [reloads, setReloads] = useState(0);

  useEffect(() => {
    if (!enabled || !projectId) {
      setState({ projectId: null, status: 'off', byStore: NONE, raise: NO_RAISE, cancel: NO_CANCEL });
      return undefined;
    }
    let cancelled = false;
    // What is on show stays while the same project is re-read; another project's goes.
    setState((prev) => (prev.projectId === projectId && prev.status === 'ready'
      ? prev
      : { projectId, status: 'loading', byStore: NONE, raise: NO_RAISE, cancel: NO_CANCEL }));
    // Inside the chain: a throw while building the request must never reach the page.
    Promise.resolve()
      .then(() => readLiveStoreLinks(projectId, () => !cancelled))
      .then(({ links, raise, cancel }) => {
        if (!cancelled) setState({ projectId, status: 'ready', byStore: groupByStore(links), raise, cancel });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const status = hiringStatusOf(err);
        if (isHiringOff(err) || status === 403) forgetHiringAccess(projectId);
        // A re-read that failed: the list already on show stays. Only a project with
        // nothing read yet shows the dash.
        setState((prev) => (prev.projectId === projectId && prev.status === 'ready'
          ? prev
          : { projectId, status: 'failed', byStore: NONE, raise: NO_RAISE, cancel: NO_CANCEL }));
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, enabled, reloads]);

  const reload = useCallback(() => setReloads((n) => n + 1), []);

  const apply = useCallback((link: HiringLink) => {
    // Only onto a list that was read: with none, one link would make every other store
    // read "No hiring linked".
    setState((prev) => (prev.projectId === projectId && prev.status === 'ready'
      ? { ...prev, byStore: withLink(prev.byStore, link) }
      : prev));
  }, [projectId]);

  // The first render after hiring comes on, or after the project changes, still holds
  // the previous state: read it as loading rather than as the other project's answer.
  const current = enabled && projectId && state.projectId === projectId;
  return {
    status: !enabled || !projectId ? 'off' : current ? state.status : 'loading',
    byStore: current ? state.byStore : NONE,
    raise: current && state.status === 'ready' ? state.raise : NO_RAISE,
    cancel: current && state.status === 'ready' ? state.cancel : NO_CANCEL,
    reload,
    apply,
  };
};
